import {
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spacer,
  Spinner,
  Text,
} from "@chakra-ui/react";
import { FormattedMessage } from "react-intl";
import { Link, useLocation } from "wouter";

import { ApiBulkExport } from "@nens/ldo/api-v1";

import { useBulkExportHook } from "../features/bulk-exports/bulk-exports-hook";
import { getBulkExportUrl } from "../features/bulk-exports/routes";

const AddToExportModalRow = ({
  exportItem,
  scenarioIds,
}: {
  exportItem: ApiBulkExport;
  scenarioIds: number[];
}) => {
  const exportUrl = getBulkExportUrl(exportItem.type, exportItem.id);
  const { addScenarioIds, exportUpdating } = useBulkExportHook(exportItem.type, exportItem.id, {
    loadScenarios: false,
  });
  const setLocation = useLocation()[1];
  const numNewIds = scenarioIds.filter((id) => !exportItem.scenario_ids.includes(id)).length;

  return (
    <Flex
      flexDirection="column"
      w="100%"
      borderColor="bij12_2.400"
      borderWidth="1px"
      borderRadius="md"
      p={4}
      gap={4}
    >
      <Flex fontWeight="semibold">
        <Link to={exportUrl}>
          {exportItem.name} ({exportItem.type})
        </Link>
        <Spacer />
        <Text>{exportItem.created_by.name}</Text>
      </Flex>
      <Flex flexDirection="row" gap={4}>
        <Text>
          <FormattedMessage
            id="bulkaction.export.modal.existingScenarios"
            defaultMessage="Existing scenario(s): {count}"
            values={{ count: exportItem.scenario_ids.length }}
          />
        </Text>
        <Text>
          <FormattedMessage
            id="bulkaction.export.modal.newlySelectedScenarios"
            defaultMessage="Newly selected scenario(s): {count}"
            values={{ count: numNewIds }}
          />
        </Text>
        <Spacer />
      </Flex>
      {exportItem.description ? <Text>{exportItem.description}</Text> : null}
      <Flex flexDirection="row" gap={4}>
        <Spacer />
        <Button onClick={() => addScenarioIds(scenarioIds)} isDisabled={numNewIds === 0}>
          <FormattedMessage
            id="bulkaction.export.modal.addToExport"
            defaultMessage="Add to export"
          />
          {exportUpdating ? <Spinner size="xs" /> : null}
        </Button>
        <Button onClick={() => setLocation(exportUrl)}>
          <FormattedMessage id="bulkaction.export.modal.goToExport" defaultMessage="Go to export" />
        </Button>
      </Flex>
    </Flex>
  );
};

export const AddToExportModal = ({
  onClose,
  isOpen,
  bulkExports,
  scenarioIds,
}: {
  onClose: () => void;
  isOpen: boolean;
  bulkExports: ApiBulkExport[];
  scenarioIds: string[];
}) => {
  const scenarioIdNumbers = scenarioIds.map((id) => Number(id)).filter((id) => !isNaN(id));

  return (
    <Modal onClose={onClose} isOpen={isOpen} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <FormattedMessage
            id="bulkaction.add_scenarios_to_exports"
            defaultMessage="Add {numScenarios} scenario(s) to exports"
            values={{ numScenarios: scenarioIds.length.toString() }}
          />
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Flex flexDirection="column" alignItems="center" gap={4}>
            {bulkExports.map((exportItem) => (
              <AddToExportModalRow
                key={exportItem.id}
                exportItem={exportItem}
                scenarioIds={scenarioIdNumbers}
              />
            ))}
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
